import {createContext, useEffect, useState, ReactNode, useContext} from "react";
import axios from "../axios-base";
import {transformPhases, ProcessedPhases} from "./phaseHelper";

export type PhaseType = 'signup' | 'qualification' | 'finale';

export interface Phase {
    _id: string;
    start: string;
    end: string;
    type: PhaseType;
}

export type FeatureFlagName =
    | 'juryVoting'
    | 'newFingerprint'
    | 'survey';

export type FeatureFlags = Partial<Record<FeatureFlagName, boolean | null | undefined>>;

export interface JurorLimits {
    voteLimit: number;
    voteLimitPerProject: number;
}

// This is what the backend sends
export interface RawClientConfiguration {
    _id: string;
    phases: Phase[];
    featureFlags: FeatureFlags;
    finalistCount: number;
    joinedCategories: boolean;
    // TODO: replace with per-juror limits once backend supports it
    jurorVoteLimit: number;
    jurorVoteLimitPerProject: number;
}

// This is what the context provides to the rest of the app
export interface ClientConfig extends Omit<RawClientConfiguration, 'phases'> {
    phases: ProcessedPhases;
}

const ConfigContext = createContext<ClientConfig | null>(null);

export const ConfigProvider =
    ({children, fallback = null}: { children: ReactNode, fallback?: ReactNode | null }) => {
        const [config, setConfig] = useState<ClientConfig | null>(null);

        useEffect(() => {
            const fetchConfig = async () => {
                try {
                    const response = await axios.get<RawClientConfiguration>('/config');

                    // Pre-process the phases right when the data arrives
                    const processedConfig: ClientConfig = {
                        ...response.data,
                        phases: transformPhases(response.data.phases),
                    };

                    setConfig(processedConfig);
                } catch (error) {
                    console.error("Failed to fetch configuration:", error);
                }
            };

            void fetchConfig();
        }, []);

        // Ensure that the component doesn't render until the config is loaded
        if (!config) {
            return fallback ?? <></>;
        }

        return (
            <ConfigContext.Provider value={config}>
                {children}
            </ConfigContext.Provider>
        );
    }

// Helper to safely get context and throw a clear error if used outside provider or while loading
const useSafeConfig = (): ClientConfig => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error("ConfigContext is null. Make sure it's wrapped in a Provider and finished loading.");
    }
    return context;
};

// Custom Hooks
export const usePhases = (): ProcessedPhases => useSafeConfig().phases;

export const useFeatureFlags = (): FeatureFlags => useSafeConfig().featureFlags;

export const useFeatureFlag = (flag: FeatureFlagName): boolean =>
    useSafeConfig().featureFlags[flag] ?? false;

export const useFinalistCount = (): number => useSafeConfig().finalistCount;

export const useJoinedCategories = (): boolean => useSafeConfig().joinedCategories;

// TODO: replace with per-juror limits once backend supports it
export const useJurorLimits = (): JurorLimits => ({
    voteLimit: useSafeConfig().jurorVoteLimit,
    voteLimitPerProject: useSafeConfig().jurorVoteLimitPerProject,
});